import { CalendarOutlined, CloseOutlined, DollarCircleOutlined, MailOutlined } from '@ant-design/icons';

import Page from '../../Components/Page/Page';
import { TextService } from '../../Services/TextService';
import { Card, Checks, Crumbs, CtaBanner, Hero, SectionHead, Shot, Split, useScrollTop } from '../Start2/Marketing2';
import '../Start2/Start2.css';

// SEO article for /medlemsregister, built from the shared Start2 marketing components.
// Claims we could not verify in the product are intentionally left out
// (automatic payment reminders, scheduled messages, open/click statistics, recurring fees).
const MemberSystemForAssociationsPage = () => {
  const GetText = TextService.GetText;
  useScrollTop();

  const problems = [
    "Excel-listor som snabbt blir inaktuella",
    "Medlemsregister som ligger utspridda hos flera personer",
    "Missade betalningar och svårigheter att följa upp medlemsavgifter",
    "Kommunikation via flera olika kanaler",
    "Tidskrävande administration inför event och aktiviteter",
  ];

  const solutions = [
    "Ett medlemsregister som alltid är aktuellt",
    "Överblick över vilka som har betalat",
    "Utskick och anmälningar från samma ställe",
  ];

  return <Page className='start2' pageTitle={GetText("PageTitle_MemberRegister")} metaDescription={GetText("PageDescription_MemberRegister")}>

    <Hero
      crumb={<Crumbs current="Medlemsregister" />}
      title="Medlemsregister för föreningar – enkelt, automatiserat och gjort för att spara tid"
      lead="Ett modernt medlemssystem som hjälper föreningar att hantera medlemmar, avgifter, event och kommunikation – allt på ett ställe."
      image={require('../../Images/Articles/medlemsregister-illustration.png')}
      imageAlt="Illustration av OneFront360 medlemsregister"
    />

    <section className="s2-section">
      <div className="s2-prose">
        <p>
          Att driva en förening ska handla om gemenskap, utveckling och aktiviteter – inte om administration,
          Excel-filer och manuella betalningar. Ett modernt medlemssystem gör det möjligt att hantera medlemmar,
          avgifter, event och kommunikation på ett sätt som både sparar tid och minskar risken för fel. OneFront360
          är utvecklat för föreningar som vill få full kontroll över sin verksamhet utan att behöva lägga timmar på
          administration.
        </p>
      </div>
    </section>

    <section className="s2-section s2-bg-soft">
      <div className="s2-container">
        <SectionHead
          title="Varför ett medlemssystem är avgörande för dagens föreningar"
          text="Föreningar växer, medlemmar byts ut, avgifter ska betalas och aktiviteter ska planeras. Många föreningar fastnar i manuella rutiner som:" />
        <div className="s2-cards s2-cards-2">
          <div className="s2-card s2-problem">
            <span className="s2-eyebrow">Problemet</span>
            <h3>Manuella rutiner som tar tid</h3>
            <ul className="s2-checks s2-checks-x">
              {problems.map(p => <li key={p}><CloseOutlined /> <span>{p}</span></li>)}
            </ul>
          </div>
          <div className="s2-card s2-solution">
            <span className="s2-eyebrow">Lösningen</span>
            <h3>Ett digitalt föreningssystem samlar allt på ett ställe</h3>
            <Checks items={solutions} />
            <p><em>Även för ideella krafter med begränsad tid.</em></p>
          </div>
        </div>
      </div>
    </section>

    <section className="s2-section">
      <div className="s2-container">
        <SectionHead title="Ett komplett medlemssystem för föreningar" />

        <Split eyebrow="Medlemsregister" title="Medlemsregister som alltid är uppdaterat" visual={<Shot src={require('../../Images/Articles/medlemsregister.png')} alt="OneFront 360 medlemsregister" />}>
          <p>Ett bra <strong>medlemsregister för föreningar</strong> ska vara enkelt att använda och alltid korrekt. I OneFront360 får du:</p>
          <Checks items={[
            "Full överblick över alla medlemmar",
            "Kontaktuppgifter, roller och grupper",
            "Historik över betalningar och aktiviteter",
            "Möjlighet att importera/exportera register",
            "Automatisk uppdatering när medlemmar ändrar sina uppgifter",
          ]} />
          <p><em>Det gör att du slipper manuellt arbete och minskar risken för fel.</em></p>
        </Split>

        <div className="s2-cards" style={{ marginTop: 48 }}>
          <Card icon={<DollarCircleOutlined />} title="Hantering av medlemsavgifter" tint="orange">
            <p>Att jaga betalningar är en av de mest tidskrävande uppgifterna i en förening. Med OneFront360 kan du:</p>
            <Checks items={[
              "Skicka påminnelser till de som inte har betalat",
              "Ta emot betalningar via kort, Swish eller faktura",
              "Få full överblick över vilka som betalat",
              "Exportera underlag till bokföring",
            ]} />
          </Card>
          <Card icon={<CalendarOutlined />} title="Event och aktiviteter" tint="blue">
            <p>Träningar, möten, kurser och andra aktiviteter – planera, boka och ta betalt:</p>
            <Checks items={[
              "Skapa event med ett klick",
              "Låta medlemmar anmäla sig online",
              "Se deltagarlistor i realtid",
              "Skicka automatiska påminnelser",
            ]} />
          </Card>
          <Card icon={<MailOutlined />} title="Kommunikation som når fram" tint="green">
            <p>All kommunikation sker från samma plattform som medlemsregistret:</p>
            <Checks items={[
              "Skicka e-post och SMS till grupper eller hela föreningen",
              "Automatiska utskick vid anmälningar och betalningar",
              "Logg över all skickad e-post och SMS",
            ]} />
          </Card>
        </div>

        <div style={{ marginTop: 48 }}>
          <SectionHead title="Passar alla typer av föreningar" text="Systemet är flexibelt och kan anpassas efter föreningens storlek och behov. OneFront360 används av föreningar inom:" />
          <div className="s2-chips">
            {["Kultur", "Intresseorganisationer", "Villa/samfällighetsföreningar", "Ideella organisationer", "Idrott", "Hembygdsföreningar", "Musikföreningar"].map(c => <span key={c}>{c}</span>)}
          </div>
        </div>
      </div>
    </section>

    <section className="s2-section s2-bg-soft">
      <div className="s2-container">
        <div className="s2-cards s2-cards-2">
          <div>
            <h2 className="s2-h2">Så hjälper OneFront360 din förening att hantera medlemmar effektivt</h2>
            <p className="s2-muted-p">Att <strong>hantera medlemmar</strong> handlar inte bara om att ha ett register. Det handlar om att skapa struktur, trygghet och enkelhet för både styrelse och medlemmar.</p>
            <Checks items={[
              "Ett centralt medlemsregister",
              "Enklare administration av grupper och sektioner",
              "Möjlighet att delegera ansvar till ledare eller gruppansvariga",
              "Full spårbarhet och historik",
            ]} />
          </div>
          <div>
            <h2 className="s2-h2">Kom igång snabbt – utan teknisk kunskap</h2>
            <p className="s2-muted-p">Ingen installation – fungerar på dator, mobil och surfplatta.</p>
            <ol className="s2-steps">
              <li>Registrera ett konto på några minuter</li>
              <li>Importera ditt medlemsregister</li>
              <li>Skapa grupper och avgifter direkt</li>
              <li>Börja ta emot betalningar samma dag</li>
            </ol>
          </div>
        </div>
      </div>
    </section>

    <CtaBanner />
  </Page>
};

export default MemberSystemForAssociationsPage;
